import { Button, Link, Text } from "north.js";
import React from "react";
import { BottomSheet } from "react-spring-bottom-sheet";
import styled from "styled-components";

import { Icon } from "src/shared/components";

import { formatTime } from "../../shared/utils/date";
import { TCheckInResult, TShift } from "../constants";

interface ResultBottomSheetProps {
  volunteerName: string | undefined;
  shifts: TShift[];
  shiftStatus: TCheckInResult;
  isOpen: boolean;
  onScanAnother: () => void;
  onClose: () => void;
}

const ResultBottomSheet: React.FC<ResultBottomSheetProps> = ({
  isOpen,
  shiftStatus,
  volunteerName,
  shifts,
  onScanAnother,
  onClose,
}) => {
  return (
    <StyledBottomSheet open={isOpen} onDismiss={onClose}>
      <ModalWrapper>
        <ModalHeader>
          {shiftStatus.success && (
            <Text type="h1">
              Check-in{" "}
              <StyledText type="h1" shiftStatus="success">
                success
              </StyledText>
              !
            </Text>
          )}
          {shiftStatus.error?.message === "Already checked in." && (
            <Text type="h1">Already checked in!</Text>
          )}
          {shiftStatus.error?.message !== "Already checked in." &&
            !shiftStatus.success && (
              <StyledText type="h1" shiftStatus="error">
                Check-in failed.
              </StyledText>
            )}
          <ShiftInfo>
            <Text type="h3">
              {shifts.length === 1
                ? `Shift: ${shifts[0].name}`
                : `Scanning for ${shifts.length} shifts`}
            </Text>
            {shifts.length === 1 && (
              <Text>
                {formatTime(shifts[0].startTime) +
                  " - " +
                  formatTime(shifts[0].displayEndTime ?? shifts[0].endTime)}
              </Text>
            )}
          </ShiftInfo>
        </ModalHeader>

        <Icon
          name={
            shiftStatus.success
              ? "check-circle-inverted-green"
              : shiftStatus.error?.message === "Already checked in."
              ? "check-circle-inverted"
              : "check-circle-inverted-red"
          }
          style={{ margin: "24px" }}
          size={
            shiftStatus.error?.message === "No ShiftSignup found"
              ? "150px"
              : "160px"
          }
        />

        {shiftStatus.error?.message === "Already checked in." ||
        shiftStatus.success ? (
          <Message>
            <span style={{ fontWeight: "bold" }}>{volunteerName} </span>
            {shiftStatus.success ? (
              <Text>
                has been checked in for{" "}
                <span style={{ fontWeight: "bold" }}>
                  {shifts.length === 1 ? "their shift" : "their shifts"}
                </span>
              </Text>
            ) : (
              <Text>has already been checked in.</Text>
            )}
          </Message>
        ) : (
          <ErrorMessageWrapper>
            <ErrorMessage>{shiftStatus.error?.message}</ErrorMessage>
            <ErrorMessage>
              Please look to the help desk, or an organizer to help address this
              issue.
            </ErrorMessage>
          </ErrorMessageWrapper>
        )}

        <ModalButtons>
          <ScanningButton color="primary" size="md" onClick={onScanAnother}>
            Scan another QR code ➜
          </ScanningButton>
          <Link onClick={onClose}>Close</Link>
        </ModalButtons>
      </ModalWrapper>
    </StyledBottomSheet>
  );
};

const StyledBottomSheet = styled(BottomSheet)`
  position: fixed;
  z-index: 10000 !important;
`;

const ModalWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  p {
    text-align: center;
  }
`;

const ShiftInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

const StyledText = styled(Text)<{
  shiftStatus: "success" | "error";
}>`
  color: ${({ theme, shiftStatus }) =>
    shiftStatus === "success"
      ? theme.globalConstants.color.textSuccess
      : theme.globalConstants.color.textDanger};
`;

const Message = styled.div`
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
  padding: 18px 23px;
  border-radius: 4px;
  margin-bottom: 36px;
  text-align: center;
`;

const ErrorMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 18px;
  margin-bottom: 36px;
`;

const ErrorMessage = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
`;

const ModalButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

const ScanningButton = styled(Button)`
  --buttonWidth: 250px;
  width: var(--buttonWidth) !important;
  left: calc(50vw - var(--buttonWidth) / 2);
  height: 42px !important;
  * button {
    color: #43afde !important;
  }
`;

export default ResultBottomSheet;
